import type { UseFormReturn } from 'react-hook-form';

import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { UserLanguage } from '~/api/user/types';

import { useUser } from '~/api/user';
import { Button } from '~/components';
import { Field, Input } from '~/components/Form';

import type { CreateEditProductFormType } from '../types';

import { addTranslationsStyle, nameFieldsStyle, nameInputRowStyle, nameInputStyle } from './productSections.css';

const productNameLanguages = ['nl', 'fr', 'en'] satisfies UserLanguage[];

type Props = {
  form: UseFormReturn<CreateEditProductFormType>;
};

/** The product name in nl/fr/en — the user's own language required, the other two optional. */
export const ProductNameFields = ({ form: { control, getValues, setFocus, setValue } }: Props) => {
  const { data: user } = useUser();
  const { t } = useTranslation(['common', 'products', 'validation']);

  const translationLanguages = productNameLanguages.filter((language) => language !== user.lang);

  const [expandedLanguages, setExpandedLanguages] = useState<UserLanguage[]>(() =>
    translationLanguages.filter((language) => !!getValues(`name.${language}`)),
  );
  // setFocus needs the field mounted, so it's deferred to the effect below.
  const [languagePendingFocus, setLanguagePendingFocus] = useState<UserLanguage | null>(null);

  const collapsedLanguages = translationLanguages.filter((language) => !expandedLanguages.includes(language));

  const addTranslation = (language: UserLanguage) => {
    setExpandedLanguages((languages) => [...languages, language]);
    setLanguagePendingFocus(language);
  };

  const removeTranslation = (language: UserLanguage) => {
    setValue(`name.${language}`, '', { shouldDirty: true });
    setExpandedLanguages((languages) => languages.filter((expandedLanguage) => expandedLanguage !== language));
  };

  useEffect(() => {
    if (!languagePendingFocus) return;

    setFocus(`name.${languagePendingFocus}`);
    setLanguagePendingFocus(null);
  }, [languagePendingFocus, setFocus]);

  return (
    <div className={nameFieldsStyle}>
      {[user.lang, ...translationLanguages.filter((language) => expandedLanguages.includes(language))].map((language) => {
        const isPrimaryLanguage = language === user.lang;

        return (
          <Controller
            control={control}
            key={language}
            name={`name.${language}`}
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
              <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                <Field.Label optional={!isPrimaryLanguage}>{t(`products:fields.name.inLanguage.${language}`)}</Field.Label>

                <div className={nameInputRowStyle}>
                  <div className={nameInputStyle}>
                    <Input
                      onBlur={onBlur}
                      onValueChange={onChange}
                      placeholder={t('products:fields.name.placeholder')}
                      ref={ref}
                      value={value}
                    />
                  </div>

                  {!isPrimaryLanguage && (
                    <Button
                      aria-label={t('common:actions.delete')}
                      emphasis="tertiary"
                      icon="Trash"
                      onClick={() => removeTranslation(language)}
                    />
                  )}
                </div>

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            )}
            rules={{
              maxLength: { message: t('validation:maxLength', { attribute: t('products:fields.name.label'), max: 255 }), value: 255 },
              ...(isPrimaryLanguage && {
                required: { value: true, message: t('validation:required') },
                validate: (value: string) => !!value.trim() || t('validation:required'),
              }),
            }}
          />
        );
      })}

      {collapsedLanguages.length > 0 && (
        <div className={addTranslationsStyle}>
          {collapsedLanguages.map((language) => (
            <Button emphasis="tertiary" icon="Add" key={language} onClick={() => addTranslation(language)}>
              {t(`products:fields.name.inLanguage.${language}`)}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
